import type { RepoPrice, RepoPrices } from "./PricingPriceSchemas";
import pricingPricesJson from "./PricingPrices.json";
import type {
  AdminStripePrice,
  AdminStripePriceChange,
  PrivateGetAdminPricingResponse,
} from "./PrivateApiSchemas";
import { PRODUCTS, USAGE_PRODUCTS } from "./Subscriptions";

/**
 * Repo copy of the CalmLens Stripe prices (packages/shared/src/PricingPrices.json), keyed by the
 * PRODUCTS key. Stripe holds the live prices; the development-only back office Pricing tab compares
 * the two, can push repo prices to Stripe (test mode only) and can pull Stripe prices into the repo.
 * Amounts are Stripe minor-unit decimal strings (e.g. pence).
 */

export type ProductKey = keyof typeof PRODUCTS;

export const PRODUCT_KEYS = Object.keys(PRODUCTS) as [ProductKey, ...ProductKey[]];

export type { RepoPrice, RepoPrices, RepoPriceTier } from "./PricingPriceSchemas";

// Not parsed with REPO_PRICES_SCHEMA here: this module reaches the landing page's pricing
// calculator, and validating would load zod in every visitor's browser. PricingPrices.test.ts
// checks the JSON against the schema instead.
export const DEFAULT_REPO_PRICES = pricingPricesJson as RepoPrices;

export const getMeterProductKey = (meter: keyof typeof USAGE_PRODUCTS) =>
  USAGE_PRODUCTS[meter].id as ProductKey;

/** Canonical form of a non-negative decimal string: "0.150" -> "0.15", "007" -> "7", "" -> "0". */
export const normaliseDecimalString = (value: string): string => {
  const [integerPart = "", fractionPart = ""] = value.trim().split(".");
  const integer = integerPart.replace(/^0+/u, "") || "0";
  const fraction = fractionPart.replace(/0+$/u, "");
  return fraction ? `${integer}.${fraction}` : integer;
};

export const decimalStringsEqual = (left: string | null, right: string | null) =>
  left === null || right === null
    ? left === right
    : normaliseDecimalString(left) === normaliseDecimalString(right);

export type PriceSyncStatus = "inSync" | "mismatch" | "missingInRepo" | "stripeUnavailable";

export interface PriceComparison {
  status: PriceSyncStatus;
  differences: string[];
  /** True when the push flow can fix the mismatch (only amounts differ). */
  pushable: boolean;
}

/** Repo representation of a Stripe price, used by "Pull from Stripe". */
export const repoPriceFromStripe = (price: AdminStripePrice): RepoPrice | null => {
  if (price.error || !price.priceId || !price.currency) return null;
  const interval = price.recurringInterval as RepoPrice["interval"];
  if (price.billingScheme === "tiered") {
    return { currency: price.currency, tiers: price.tiers, interval };
  }
  if (price.unitAmountDecimal === null) return null;
  return { currency: price.currency, unitAmountDecimal: price.unitAmountDecimal, interval };
};

/**
 * Compares a repo price with the live Stripe price. Amounts are compared as decimal strings,
 * tiers tier by tier, and currency or interval differences count as mismatches (which the push
 * flow cannot fix, because it only replaces amounts).
 */
export const compareRepoPrice = (
  repoPrice: RepoPrice | undefined,
  stripePrice: AdminStripePrice,
): PriceComparison => {
  if (stripePrice.error || !stripePrice.priceId) {
    return { status: "stripeUnavailable", differences: [], pushable: false };
  }
  if (!repoPrice) {
    return { status: "missingInRepo", differences: ["Not in repo"], pushable: false };
  }

  const differences: string[] = [];
  let amountsOnly = true;
  if (repoPrice.currency !== stripePrice.currency?.toLowerCase()) {
    differences.push(`Currency: repo ${repoPrice.currency}, Stripe ${stripePrice.currency}`);
    amountsOnly = false;
  }
  if (repoPrice.interval !== stripePrice.recurringInterval) {
    differences.push(
      `Interval: repo ${repoPrice.interval ?? "none"}, Stripe ${stripePrice.recurringInterval ?? "none"}`,
    );
    amountsOnly = false;
  }

  const stripeTiered = stripePrice.billingScheme === "tiered";
  if (stripeTiered !== (repoPrice.tiers !== undefined)) {
    differences.push(
      `Billing scheme: repo ${repoPrice.tiers ? "tiered" : "per_unit"}, Stripe ${stripePrice.billingScheme}`,
    );
    amountsOnly = false;
  } else if (repoPrice.tiers) {
    const tierCount = Math.max(repoPrice.tiers.length, stripePrice.tiers.length);
    for (let index = 0; index < tierCount; index++) {
      const repoTier = repoPrice.tiers[index];
      const stripeTier = stripePrice.tiers[index];
      if (!repoTier || !stripeTier) {
        differences.push(`Tier ${index + 1}: only in ${repoTier ? "repo" : "Stripe"}`);
        continue;
      }
      if (repoTier.upTo !== stripeTier.upTo) {
        differences.push(
          `Tier ${index + 1} up to: repo ${repoTier.upTo ?? "∞"}, Stripe ${stripeTier.upTo ?? "∞"}`,
        );
      }
      if (!decimalStringsEqual(repoTier.unitAmountDecimal, stripeTier.unitAmountDecimal)) {
        differences.push(
          `Tier ${index + 1} unit amount: repo ${repoTier.unitAmountDecimal}, Stripe ${stripeTier.unitAmountDecimal}`,
        );
      }
      if (!decimalStringsEqual(repoTier.flatAmountDecimal, stripeTier.flatAmountDecimal)) {
        differences.push(
          `Tier ${index + 1} flat amount: repo ${repoTier.flatAmountDecimal}, Stripe ${stripeTier.flatAmountDecimal}`,
        );
      }
    }
  } else if (
    !decimalStringsEqual(repoPrice.unitAmountDecimal ?? null, stripePrice.unitAmountDecimal)
  ) {
    differences.push(
      `Unit amount: repo ${repoPrice.unitAmountDecimal}, Stripe ${stripePrice.unitAmountDecimal}`,
    );
  }

  return differences.length === 0
    ? { status: "inSync", differences, pushable: false }
    : {
        status: "mismatch",
        differences,
        pushable: amountsOnly && !stripePrice.livemode,
      };
};

export interface ProductPriceComparison extends PriceComparison {
  productKey: ProductKey;
  stripePrice: AdminStripePrice;
  repoPrice: RepoPrice | undefined;
}

/** One comparison per CalmLens product (without duplicates). */
export const compareRepoPrices = (
  pricing: PrivateGetAdminPricingResponse,
  repoPrices: RepoPrices,
): ProductPriceComparison[] => {
  const items = pricing.meters.map(({ meter, price }) => ({
    productKey: getMeterProductKey(meter),
    stripePrice: price,
  }));
  const seen = new Set<ProductKey>();
  return items.flatMap(({ productKey, stripePrice }) => {
    if (seen.has(productKey)) return [];
    seen.add(productKey);
    const repoPrice = repoPrices[productKey];
    return [{ productKey, stripePrice, repoPrice, ...compareRepoPrice(repoPrice, stripePrice) }];
  });
};

/** Changes that push mismatched repo prices to Stripe (amount-only mismatches). */
export const buildRepoPushChanges = (
  comparisons: ProductPriceComparison[],
): AdminStripePriceChange[] =>
  comparisons.flatMap(({ pushable, repoPrice, stripePrice }): AdminStripePriceChange[] => {
    if (!pushable || !repoPrice) return [];
    if (repoPrice.tiers) return [{ productId: stripePrice.productId, tiers: repoPrice.tiers }];
    if (repoPrice.unitAmountDecimal === undefined) return [];
    return [{ productId: stripePrice.productId, unitAmountDecimal: repoPrice.unitAmountDecimal }];
  });

/** Overwrites the repo prices of the given products with the live Stripe prices. */
export const pullStripePrices = (
  repoPrices: RepoPrices,
  comparisons: ProductPriceComparison[],
  productKeys: ProductKey[],
): RepoPrices => {
  const next: RepoPrices = { ...repoPrices };
  for (const comparison of comparisons) {
    if (!productKeys.includes(comparison.productKey)) continue;
    const pulled = repoPriceFromStripe(comparison.stripePrice);
    if (pulled) next[comparison.productKey] = pulled;
  }
  return next;
};

/** Returns a copy of the repo prices with one amount replaced (tierIndex null = unit amount). */
export const setRepoPriceAmount = (
  repoPrices: RepoPrices,
  productKey: ProductKey,
  tierIndex: number | null,
  value: string,
): RepoPrices => {
  const current = repoPrices[productKey];
  if (!current) return repoPrices;
  const updated: RepoPrice =
    tierIndex === null
      ? { ...current, unitAmountDecimal: value }
      : {
          ...current,
          tiers: current.tiers?.map((tier, index) =>
            index === tierIndex ? { ...tier, unitAmountDecimal: value } : tier,
          ),
        };
  return { ...repoPrices, [productKey]: updated };
};
